import { Book, FileText, Camera, Trophy, Calendar, BookCheck, School } from 'lucide-react';
import { LessonType, Role, LessonTypeConfig } from '../core/types';
import { SlideType } from '../core/types/slide.types';

/**
 * Configuration for each lesson type
 */
export const lessonTypeConfigs: Record<LessonType, LessonTypeConfig> = {
  // ===============================
  // LESSON - Educational lessons
  // ===============================
  [LessonType.LESSON]: {
    type: LessonType.LESSON,
    displayName: 'Lecție',
    pluralName: 'Lecții',
    icon: Book,

    features: {
      allowedSlideTypes: [
        SlideType.QUESTION,
        SlideType.VIDEO_PREVIEW,
        SlideType.INFO,
        SlideType.TEXT,
      ],
      requiresApproval: false,
      allowsMultipleAttempts: true,
      hasDeadline: false,
      awardsPoints: true,
      maxSlides: 50,
      minSlides: 1,
    },

    permissions: {
      create: [Role.ADMIN, Role.DBEST],
      edit: [Role.ADMIN, Role.DBEST],
      complete: [Role.ADMIN, Role.DBEST],
      view: [Role.ADMIN, Role.DBEST, Role.PROFESOR, Role.ELEV],
      approve: [],
    },

    validation: {
      titleMaxLength: 200,
      descriptionMaxLength: 1000,
      customRules: [
        {
          field: 'slides',
          rule: (value: any, _lesson: any) => value && value.length >= 1,
          message: 'Lecția trebuie să aibă cel puțin un slide',
        },
      ],
    },
  },

  // ===============================
  // HOMEWORK - Homework assignments
  // ===============================
  [LessonType.HOMEWORK]: {
    type: LessonType.HOMEWORK,
    displayName: 'Exercitiu Pentru Acasa',
    pluralName: 'Exercitii Pentru Acasa',
    icon: FileText,

    features: {
      allowedSlideTypes: [
        SlideType.QUESTION,
        SlideType.TEXT,
        SlideType.IMAGE_UPLOAD,
        SlideType.VIDEO_PREVIEW,
      ],
      requiresApproval: false,
      allowsMultipleAttempts: false,
      hasDeadline: true,
      awardsPoints: true,
      maxSlides: 50,
      minSlides: 1,
    },

    permissions: {
      create: [Role.ADMIN, Role.DBEST],
      edit: [Role.ADMIN, Role.DBEST],
      complete: [Role.ADMIN, Role.DBEST, Role.ELEV],
      view: [Role.ADMIN, Role.DBEST, Role.PROFESOR, Role.ELEV],
      approve: [Role.ADMIN, Role.DBEST],
    },

    validation: {
      titleMaxLength: 200,
      descriptionMaxLength: 1000,
      customRules: [
        {
          field: 'slides',
          rule: (value: any, _lesson: any) => value && value.length >= 1,
          message: 'Tema trebuie să aibă cel puțin un slide',
        },
        {
          field: 'deadline',
          rule: (value: any, lesson: any) =>
            lesson.type === LessonType.HOMEWORK ? Boolean(value) : true,
          message: 'Tema pentru acasă trebuie să aibă un termen limită',
        },
        {
          field: 'deadline',
          rule: (value: any, lesson: any) => {
            if (lesson.type !== LessonType.HOMEWORK || !value) return true;

            // DEBUG: Log validation details
            console.log('🔍 HOMEWORK Deadline Validation:');
            console.log('  - value:', value);
            console.log('  - value type:', typeof value);

            // Deadline must be at least 1 hour in the future
            const oneHourFromNow = Date.now() + (60 * 60 * 1000);
            console.log('  - oneHourFromNow (timestamp):', oneHourFromNow);
            console.log('  - oneHourFromNow (date):', new Date(oneHourFromNow).toISOString());

            // Convert value to timestamp if it's a string
            const valueTimestamp = typeof value === 'string' ? new Date(value).getTime() : value;
            console.log('  - valueTimestamp:', valueTimestamp);
            console.log('  - valueTimestamp (date):', new Date(valueTimestamp).toISOString());

            const isValid = valueTimestamp > oneHourFromNow;
            console.log('  - isValid:', isValid, `(${valueTimestamp} > ${oneHourFromNow})`);

            return isValid;
          },
          message: 'Termenul limită trebuie să fie în viitor (minimum 1 oră de acum)',
        },
      ],
    },
  },

  // ===============================
  // PRACTICAL_ACTIVITY - Activities requiring photo uploads
  // ===============================
  [LessonType.PRACTICAL_ACTIVITY]: {
    type: LessonType.PRACTICAL_ACTIVITY,
    displayName: 'Activitate practică',
    pluralName: 'Activități practice',
    icon: Camera,

    features: {
      allowedSlideTypes: [
        SlideType.IMAGE_UPLOAD,
        SlideType.TEXT,
        SlideType.INFO,
      ],
      requiresApproval: true,
      allowsMultipleAttempts: false,
      hasDeadline: false,
      awardsPoints: true,
      maxSlides: 10,
      minSlides: 1,
    },

    permissions: {
      create: [Role.ADMIN, Role.DBEST],
      edit: [Role.ADMIN, Role.DBEST],
      complete: [Role.ADMIN, Role.DBEST, Role.PROFESOR],
      view: [Role.ADMIN, Role.DBEST, Role.PROFESOR, Role.ELEV],
      approve: [Role.ADMIN, Role.DBEST],
    },

    validation: {
      titleMaxLength: 200,
      descriptionMaxLength: 1000,
      customRules: [
        {
          field: 'slides',
          rule: (value: any, _lesson: any) => {
            if (!value || value.length === 0) return false;
            // Must have at least one IMAGE_UPLOAD slide
            return value.some((slide: any) => slide.type === SlideType.IMAGE_UPLOAD);
          },
          message: 'Activitatea practică trebuie să aibă cel puțin un slide de încărcare imagini',
        },
      ],
    },
  },

  // ===============================
  // MONTHLY_CHALLENGE - Monthly challenges
  // ===============================
  [LessonType.MONTHLY_CHALLENGE]: {
    type: LessonType.MONTHLY_CHALLENGE,
    displayName: 'Provocare',
    pluralName: 'Provocări',
    icon: Trophy,

    features: {
      allowedSlideTypes: [
        SlideType.QUESTION,
        SlideType.IMAGE_UPLOAD,
        SlideType.TEXT,
        SlideType.VIDEO_PREVIEW,
        SlideType.INFO,
      ],
      requiresApproval: true,
      allowsMultipleAttempts: false,
      hasDeadline: true,
      awardsPoints: true,
      maxSlides: 30,
      minSlides: 5,
    },

    permissions: {
      create: [Role.ADMIN, Role.DBEST],
      edit: [Role.ADMIN, Role.DBEST],
      complete: [Role.ADMIN, Role.DBEST, Role.ELEV],
      view: [Role.ADMIN, Role.DBEST, Role.PROFESOR, Role.ELEV],
      approve: [Role.ADMIN, Role.DBEST],
    },

    validation: {
      titleMaxLength: 200,
      descriptionMaxLength: 1000,
      customRules: [
        {
          field: 'slides',
          rule: (value: any, _lesson: any) => value && value.length >= 5,
          message: 'Provocarea lunară trebuie să aibă cel puțin 5 slide-uri',
        },
        {
          field: 'deadline',
          rule: (value: any, lesson: any) =>
            lesson.type === LessonType.MONTHLY_CHALLENGE ? Boolean(value) : true,
          message: 'Provocarea lunară trebuie să aibă un termen limită',
        },
        {
          field: 'deadline',
          rule: (value: any, lesson: any) => {
            if (lesson.type !== LessonType.MONTHLY_CHALLENGE || !value) return true;
            // Deadline must be at least 1 hour in the future
            const oneHourFromNow = Date.now() + (60 * 60 * 1000);
            return value > oneHourFromNow;
          },
          message: 'Termenul limită trebuie să fie în viitor (minimum 1 oră de acum)',
        },
      ],
    },
  },

  // ===============================
  // DAILY_CHALLENGE - Daily challenges (disabled but supported)
  // ===============================
  [LessonType.DAILY_CHALLENGE]: {
    type: LessonType.DAILY_CHALLENGE,
    displayName: 'Provocare zilnică',
    pluralName: 'Provocări zilnice',
    icon: Calendar,

    features: {
      allowedSlideTypes: [
        SlideType.QUESTION,
        SlideType.IMAGE_UPLOAD,
        SlideType.TEXT,
        SlideType.INFO,
      ],
      requiresApproval: true,
      allowsMultipleAttempts: false,
      hasDeadline: true,
      awardsPoints: true,
      maxSlides: 10,
      minSlides: 1,
    },

    permissions: {
      create: [Role.ADMIN, Role.DBEST],
      edit: [Role.ADMIN, Role.DBEST],
      complete: [Role.ADMIN, Role.DBEST, Role.PROFESOR],
      view: [Role.ADMIN, Role.DBEST, Role.PROFESOR, Role.ELEV],
      approve: [Role.ADMIN, Role.DBEST],
    },

    validation: {
      titleMaxLength: 200,
      descriptionMaxLength: 1000,
      customRules: [
        {
          field: 'slides',
          rule: (value: any, _lesson: any) => value && value.length >= 1,
          message: 'Provocarea zilnică trebuie să aibă cel puțin un slide',
        },
        {
          field: 'deadline',
          rule: (value: any, lesson: any) => {
            if (lesson.type !== LessonType.DAILY_CHALLENGE) return true;
            if (!value) return false;
            // Deadline must be at least 1 hour in the future
            const oneHourFromNow = Date.now() + (60 * 60 * 1000);
            return value > oneHourFromNow;
          },
          message: 'Termenul limită trebuie să fie în viitor (minimum 1 oră de acum)',
        },
        {
          field: 'deadline',
          rule: (value: any, lesson: any) => {
            if (lesson.type !== LessonType.DAILY_CHALLENGE) return true;
            if (!value) return false;
            // Check if deadline is within 24 hours
            const deadline = new Date(value);
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return deadline <= tomorrow;
          },
          message: 'Provocarea zilnică trebuie să expire în maximum 24 de ore',
        },
      ],
    },
  },

  // ===============================
  // CLASS_EXERCISE - In-class exercises for professors
  // ===============================
  [LessonType.CLASS_EXERCISE]: {
    type: LessonType.CLASS_EXERCISE,
    displayName: 'Exercitiu Pentru Clasa',
    pluralName: 'Exercitii Pentru Clasa',
    icon: BookCheck,

    features: {
      allowedSlideTypes: [
        SlideType.VIDEO_PREVIEW,
        SlideType.IMAGE_UPLOAD,
        SlideType.INFO,
        SlideType.TEXT,
      ],
      requiresApproval: true,
      allowsMultipleAttempts: false,
      hasDeadline: false,
      awardsPoints: false,
      maxSlides: 10,
      minSlides: 1,
    },

    permissions: {
      create: [Role.ADMIN, Role.DBEST],
      edit: [Role.ADMIN, Role.DBEST],
      complete: [Role.ADMIN, Role.DBEST, Role.PROFESOR], // KEY: Professors can complete
      view: [Role.ADMIN, Role.DBEST, Role.PROFESOR],
      approve: [Role.ADMIN, Role.DBEST], // KEY: Only admins approve (not professors)
    },

    validation: {
      titleMaxLength: 200,
      descriptionMaxLength: 1000,
      customRules: [
        {
          field: 'slides',
          rule: (value: any, _lesson: any) => {
            if (!value || value.length === 0) return false;
            // Must have at least one VIDEO_PREVIEW or IMAGE_UPLOAD slide
            return value.some((slide: any) =>
              slide.type === SlideType.VIDEO_PREVIEW ||
              slide.type === SlideType.IMAGE_UPLOAD
            );
          },
          message: 'Exercițiul trebuie să aibă cel puțin un slide de tip video sau încărcare imagini',
        },
      ],
    },
  },

  // ===============================
  // SCHOOL_CHALLENGE - Provocari pentru scoli (2026-10-02)
  // ===============================
  // Se depune de PROFESOR prin ecranul de dovezi (ca activitatea practica), nu prin playerul de
  // lectii: de aceea `complete` e gol. O singura depunere punctata per scoala; punctajul provocarii
  // e `punctajMaxim`, termenul e `deadline`.
  [LessonType.SCHOOL_CHALLENGE]: {
    type: LessonType.SCHOOL_CHALLENGE,
    displayName: 'Provocare pentru școală',
    pluralName: 'Provocări pentru Școli',
    icon: School,

    features: {
      allowedSlideTypes: [
        SlideType.IMAGE_UPLOAD,
        SlideType.TEXT,
        SlideType.INFO,
      ],
      requiresApproval: true,
      allowsMultipleAttempts: false,
      hasDeadline: true,
      awardsPoints: true,
      maxSlides: 10,
      minSlides: 1,
    },

    permissions: {
      create: [Role.ADMIN, Role.DBEST],
      edit: [Role.ADMIN, Role.DBEST],
      complete: [],
      view: [Role.ADMIN, Role.DBEST, Role.PROFESOR],
      approve: [Role.ADMIN, Role.DBEST],
    },

    validation: {
      titleMaxLength: 200,
      descriptionMaxLength: 1000,
      customRules: [
        {
          field: 'slides',
          rule: (value: any, _lesson: any) => {
            if (!value || value.length === 0) return false;
            return value.some((slide: any) => slide.type === SlideType.IMAGE_UPLOAD);
          },
          message: 'Provocarea trebuie să aibă cel puțin un slide de încărcare imagini',
        },
      ],
    },
  },
};

/**
 * Get configuration for a specific lesson type
 */
export function getLessonTypeConfig(type: LessonType): LessonTypeConfig {
  const config = lessonTypeConfigs[type];
  if (!config) {
    throw new Error(`Unknown lesson type: ${type}`);
  }
  return config;
}

/**
 * Get all enabled lesson type configurations
 */
export function getEnabledLessonTypes(): LessonTypeConfig[] {
  // Currently all types except DAILY_CHALLENGE are enabled
  return Object.values(lessonTypeConfigs).filter(
    (config) => config.type !== LessonType.DAILY_CHALLENGE
  );
}

/**
 * Check if a user has permission for an action on a lesson type
 */
export function hasPermission(
  userRole: Role,
  lessonType: LessonType,
  action: keyof LessonTypeConfig['permissions']
): boolean {
  const config = getLessonTypeConfig(lessonType);
  return config.permissions[action].includes(userRole);
}

/**
 * Get allowed slide types for a lesson type
 */
export function getAllowedSlideTypes(lessonType: LessonType): string[] {
  const config = getLessonTypeConfig(lessonType);
  return config.features.allowedSlideTypes;
}

/**
 * Check if a slide type is allowed for a lesson type
 */
export function isSlideTypeAllowed(
  lessonType: LessonType,
  slideType: string
): boolean {
  const allowed = getAllowedSlideTypes(lessonType);
  return allowed.includes(slideType);
}

/**
 * Validate a lesson against its type configuration
 */
export function validateLesson(lesson: any): {
  valid: boolean;
  errors: string[];
} {
  const config = getLessonTypeConfig(lesson.type);
  const errors: string[] = [];

  // Title validation
  if (!lesson.title || lesson.title.trim().length === 0) {
    errors.push('Titlul este obligatoriu');
  } else if (lesson.title.length > config.validation.titleMaxLength) {
    errors.push(`Titlul nu poate depăși ${config.validation.titleMaxLength} caractere`);
  }

  // Description validation
  if (!lesson.description || lesson.description.trim().length === 0) {
    errors.push('Descrierea este obligatorie');
  } else if (lesson.description.length > config.validation.descriptionMaxLength) {
    errors.push(`Descrierea nu poate depăși ${config.validation.descriptionMaxLength} caractere`);
  }

  // Slides validation
  const slideCount = lesson.slides?.length || 0;
  if (slideCount < (config.features?.minSlides || 1)) {
    errors.push(`Minimum ${config.features?.minSlides || 1} slide-uri necesare`);
  }
  if (config.features.maxSlides && slideCount > config.features.maxSlides) {
    errors.push(`Maximum ${config.features.maxSlides} slide-uri permise`);
  }

  // Custom rules validation
  if (config.validation.customRules) {
    config.validation.customRules.forEach((rule) => {
      const value = lesson[rule.field];
      if (!rule.rule(value, lesson)) {
        errors.push(rule.message);
      }
    });
  }

  // Validate slide types
  if (lesson.slides) {
    lesson.slides.forEach((slide: any, index: number) => {
      if (!config.features.allowedSlideTypes.includes(slide.type)) {
        errors.push(`Slide ${index + 1}: Tipul "${slide.type}" nu este permis pentru ${config.displayName}`);
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

